import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { WEB3FORMS_KEY, PROJECT_TYPES } from "../siteConfig";

const inputCls =
  "w-full border border-forest/20 bg-limestone px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/40 transition-colors focus:border-forest focus:outline-none";

const Field = ({ label, required, optional, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-forest">
      {label} {required && <span className="text-ochre">*</span>}
      {optional && <span className="ml-1 font-normal normal-case tracking-normal text-charcoal/50">(optional)</span>}
    </label>
    {children}
  </div>
);

export const EnquiryForm = () => {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error | demo
  const live = Boolean(WEB3FORMS_KEY);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!live) {
      setStatus("demo");
      return;
    }
    setStatus("sending");
    const data = new FormData(e.target);
    data.append("access_key", WEB3FORMS_KEY);
    data.append("subject", "New project enquiry — Cotswold Drone Mapping");
    try {
      const res = await fetch("https://api.web3forms.com/submit", { method: "POST", body: data });
      const json = await res.json();
      if (json.success) {
        setStatus("success");
        e.target.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        data-testid="enquiry-success-message"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-start gap-4 border border-forest/20 bg-sage-soft/50 p-8"
      >
        <CheckCircle2 className="h-8 w-8 text-forest" strokeWidth={1.5} />
        <p className="font-serif text-2xl text-forest">Thanks for getting in touch.</p>
        <p className="text-sm leading-relaxed text-charcoal/80">
          Your enquiry has been received. We'll be in contact as soon as we can.
        </p>
      </motion.div>
    );
  }

  return (
    <form data-testid="enquiry-form" onSubmit={handleSubmit} className="space-y-6">
      {!live && (
        <div
          data-testid="enquiry-demo-notice"
          className="flex items-start gap-3 border border-ochre/40 bg-ochre/10 p-4 text-sm leading-relaxed text-charcoal"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-ochre" />
          <p>
            <strong>Demonstration mode:</strong> this form is not yet connected to an email service, so nothing
            you send will be delivered. It will go live as soon as a free Web3Forms key is added.
          </p>
        </div>
      )}

      <input type="checkbox" name="botcheck" className="hidden" tabIndex={-1} autoComplete="off" />

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Name" required htmlFor="name">
          <input id="name" name="name" type="text" required data-testid="enquiry-name-input" className={inputCls} placeholder="Your full name" />
        </Field>
        <Field label="Organisation / estate" optional htmlFor="organisation">
          <input id="organisation" name="organisation" type="text" data-testid="enquiry-organisation-input" className={inputCls} placeholder="Estate or organisation name" />
        </Field>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Email address" required htmlFor="email">
          <input id="email" name="email" type="email" required data-testid="enquiry-email-input" className={inputCls} placeholder="you@example.co.uk" />
        </Field>
        <Field label="Site location / postcode" required htmlFor="location">
          <input id="location" name="location" type="text" required data-testid="enquiry-location-input" className={inputCls} placeholder="e.g. near Northleach, GL54" />
        </Field>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="What do you need help with?" required htmlFor="projectType">
          <select id="projectType" name="projectType" required defaultValue="" data-testid="enquiry-project-type-select" className={inputCls}>
            <option value="" disabled>Select an option</option>
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Approximate area" optional htmlFor="area">
          <input id="area" name="area" type="text" data-testid="enquiry-area-input" className={inputCls} placeholder="e.g. 200 acres, three fields" />
        </Field>
      </div>

      <Field label="Tell us a little about the project" required htmlFor="message">
        <textarea id="message" name="message" rows={5} required data-testid="enquiry-message-input" className={inputCls} placeholder="What would you like to understand or record about your land?" />
      </Field>

      <Field label="Preferred contact method" optional htmlFor="preferredContact">
        <select id="preferredContact" name="preferredContact" defaultValue="" data-testid="enquiry-contact-method-select" className={inputCls}>
          <option value="">No preference</option>
          <option value="Email">Email</option>
          <option value="Phone">Phone</option>
        </select>
      </Field>

      <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-charcoal/80">
        <input type="checkbox" name="privacy" required data-testid="enquiry-privacy-checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#244B3B]" />
        <span>
          I'm happy for Cotswold Drone Mapping to use these details to respond to my enquiry, as described in
          the <Link to="/privacy" data-testid="enquiry-privacy-link" className="underline decoration-ochre underline-offset-2 hover:text-forest">privacy policy</Link>. *
        </span>
      </label>

      <AnimatePresence>
        {status === "demo" && (
          <motion.p
            data-testid="enquiry-demo-submitted-notice"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-ochre/40 bg-ochre/10 p-4 text-sm text-charcoal"
          >
            This form is in demonstration mode — no enquiry has been sent.
          </motion.p>
        )}
        {status === "error" && (
          <motion.p
            data-testid="enquiry-error-message"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 border border-red-800/30 bg-red-800/10 p-4 text-sm text-charcoal"
          >
            <AlertCircle className="h-4 w-4 text-red-900" /> Something went wrong sending your enquiry. Please
            try again in a moment.
          </motion.p>
        )}
      </AnimatePresence>

      <button
        type="submit"
        data-testid="enquiry-submit-button"
        disabled={status === "sending"}
        className="group inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-limestone transition-colors hover:bg-forest-deep disabled:opacity-60"
      >
        {status === "sending" ? "Sending…" : "Send project enquiry"}
        <Send className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </button>
    </form>
  );
};
